import client from '../../shared/api/client';
import {
  PaginatedResponse,
  User,
  UserFull,
  UserFullProfile,
  UserVerificationField,
} from '../../shared/types';

export type UserSortField = 'id' | 'name' | 'email' | 'role' | 'is_active' | 'created_at';

export interface GetUsersParams {
  page?: number;
  perPage?: number;
  role?: 'user' | 'admin' | '';
  sortBy?: UserSortField;
  sortDir?: 'asc' | 'desc';
  search?: string;
}

export const getUsers = async (params: GetUsersParams = {}): Promise<PaginatedResponse<User>> => {
  const { page = 1, perPage = 15, role, sortBy, sortDir, search } = params;
  const response = await client.get<PaginatedResponse<User>>('/users', {
    params: {
      page,
      per_page: perPage,
      ...(role ? { role } : {}),
      ...(sortBy ? { sort_by: sortBy, sort_dir: sortDir ?? 'asc' } : {}),
      ...(search ? { search } : {}),
    },
  });
  return response.data;
};

export const getUser = async (id: number): Promise<User> => {
  const response = await client.get<User>(`/users/${id}`);
  return response.data;
};

export interface CreateUserResponse {
  user: User;
  credentials_delivered?: { email?: boolean; sms?: boolean };
}

export const createUser = async (data: {
  name: string;
  email: string;
  password: string;
  role?: string;
}): Promise<CreateUserResponse> => {
  const response = await client.post<CreateUserResponse | User>('/users', data);
  const body = response.data as CreateUserResponse | (User & Partial<CreateUserResponse>);
  if ('user' in body && body.user) {
    return body as CreateUserResponse;
  }
  return { user: body as User };
};

export interface UpdateUserResponse {
  user: User;
  credentials_delivered?: { email?: boolean; sms?: boolean };
}

export const updateUser = async (
  id: number,
  data: {
    name?: string;
    role?: string;
    is_active?: boolean;
    password?: string;
  }
): Promise<UpdateUserResponse> => {
  const response = await client.put<UpdateUserResponse | User>(`/users/${id}`, data);
  const body = response.data as UpdateUserResponse | (User & Partial<UpdateUserResponse>);
  if ('user' in body && body.user) {
    return body as UpdateUserResponse;
  }
  return { user: body as User };
};

export const toggleUserStatus = async (id: number, isActive: boolean): Promise<User> => {
  const response = await client.patch<User>(`/users/${id}/status`, { is_active: isActive });
  return response.data;
};

export const getUserFullProfile = async (id: number): Promise<UserFullProfile> => {
  const response = await client.get<UserFullProfile>(`/users/${id}/full-profile`);
  return response.data;
};

export const updateUserVerifications = async (
  id: number,
  data: Partial<Record<UserVerificationField, boolean>>,
): Promise<UserFull> => {
  const response = await client.post<UserFull>(`/users/${id}/verifications`, data);
  return response.data;
};
